import React from "react";
import { Link } from "react-router-dom";

// CSS
import "../../styles/authPage.scss";

// Components
import Icon from "../Icon";

// Helper
import { brandingOf } from "../../Helper/Branding";
import { t } from "../../Helper/i18n";

// The white half of the auth sheet. One component rather than the same markup
// in both pages: the two routes are one screen, and only the form in the teal
// half differs -- so this half must not be able to drift.
//
// It is built as three bands (top / middle / foot) pushed apart over the full
// height. Centring the lot as a single block left a dead gap above the back
// link on a tall window.
// Short lines: the four sit side by side, so each tile is a quarter of the
// band and there is no room for a sentence.
//
// `tone` is the dashboard's tile tone (see .ui-tile in its _ui.scss) -- each
// card keeps its own hue so the row reads as four different things at a
// glance, rather than four of the same card.
// Titles and texts are i18n keys, read at render (the theme sets the language after this module loads).
const PERKS = [
  { icon: "play", tone: "is-teal", title: "perkLessons", text: "perkLessonsText" },
  { icon: "award", tone: "is-gold", title: "perkExams", text: "perkExamsText" },
  { icon: "file", tone: "is-green", title: "perkFiles", text: "perkFilesText" },
  { icon: "chart", tone: "is-red", title: "perkProgress", text: "perkProgressText" },
];

// With no site header on this screen, the white half carries the only links
// out to the rest of the site.
const LINKS = [
  { to: "/courses", label: "courses" },
  { to: "/gallery", label: "gallery" },
  { to: "/contact", label: "getInTouch" },
];

/** `lede` is the one line that differs: it names signing in, or registering. */
const AuthAside = ({ tenant, lede }) => {
  const teacherName = (tenant && tenant.name) || "";
  const siteTitle = brandingOf(tenant).siteTitle;

  const initials = String(teacherName)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase();

  return (
    <div className="au-aside">
      <div className="au-asideface">
        <div className="au-asidetop">
          {/* The way back to the site lives under the form (.au-home); a
              second copy up here was the same link twice on one screen. */}
          <Link to="/" className="mu-brand">
            <span className="mu-glyph" aria-hidden="true">
              {tenant && tenant.image ? <img src={tenant.image} alt="" /> : initials || "M"}
            </span>
            <span className="mu-nm">
              {teacherName}
              {siteTitle ? <small className="mu-sub">{siteTitle}</small> : null}
            </span>
          </Link>
        </div>

        <div className="au-asidemid">
          <div className="au-pitch">
            <span className="au-tag">
              <i className="dot" aria-hidden="true"></i>
              {t("authTag")}
            </span>
            <h2>
              {t("authTitleA")} <em>{t("authTitleB")}</em>
            </h2>
            <p>{lede}</p>
          </div>

          <ul className="au-perks">
            {PERKS.map((p, i) => (
              <li key={p.title} className={p.tone} style={{ "--i": i }}>
                <span className="ic" aria-hidden="true"><Icon name={p.icon} /></span>
                <span className="tx">
                  <b>{t(p.title)}</b>
                  <span>{t(p.text)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="au-asidefoot">
          <p className="au-trust">
            <span className="ic" aria-hidden="true"><Icon name="lock" /></span>
            {t("authTrust")}
          </p>

          <nav className="au-links" aria-label={t("siteLinks")}>
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to}>
                {t(l.label)}
                <Icon name="arrow" aria-hidden="true" />
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default AuthAside;
